import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userState } from "./userSlice";
import { showNotification } from "../../components/Notification";
import { formatLabel } from "../../uitls/Utils";
import { sendOTP } from "../../api/userApi";
import { Email as EmailIcon } from "@mui/icons-material";
import { Link as MuiLink } from "@mui/material";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitReferenceState, setSubmitReferenceState] = useState(false);

  const stateOfUser = useSelector(userState);

  const requiredFields = {
    emailAddress: true,
  };

  useEffect(() => {
    if (
      !stateOfUser.error &&
      submitReferenceState &&
      stateOfUser.status === "succeeded"
    ) {
      showNotification("success", "Verification Code sent successfully");
      navigate("/reset-password", {
        state: { email: formValues?.emailAddress },
      });
    }
    setSubmitReferenceState(false);
  }, [
    submitReferenceState,
    stateOfUser.error,
    stateOfUser.status,
    formValues?.emailAddress,
    navigate,
  ]);

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(requiredFields).forEach((key) => {
      if (requiredFields[key]) {
        const value = formValues[key];
        if (typeof value === "string") {
          const trimmedValue = value.trim();
          if (!trimmedValue) {
            newErrors[key] = `${formatLabel(key)} is required`;
          }
          if (trimmedValue && key === "emailAddress") {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(trimmedValue.trim())) {
              newErrors[key] = "Invalid email format";
            }
          }
        } else if (value === null || value === undefined || value === "") {
          newErrors[key] = `${formatLabel(key)} is required`;
        }
      }
    });
    setFormErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      dispatch(sendOTP(trimObjectValues({ email: formValues?.emailAddress })))
        .unwrap()
        .then(() => {
          setSubmitReferenceState(true);
        })
        .catch((error) => {
          console.error("Error:", error);
          showNotification(
            "error",
            error?.message || "Something went wrong..."
          );
        });
    }
  };

  const handleChange = (name) => (e) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: e.target.value }));

    // Extract error message logic into independent statements
    let errorMessage = "";
    if (requiredFields[name]) {
      if (e.target.value.trim()) {
        errorMessage = "";
      } else {
        errorMessage = `${formatLabel(name)} is required`;
      }
    } else {
      errorMessage = "";
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
  };

  return (
    <div className="min-h-screen brand-header flex items-center justify-center p-4 md:p-8">
      {/* Main Container with Shadow */}
      <div className="w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden relative">
        <div className="flex flex-col lg:flex-row min-h-[400px] lg:min-h-[600px]">
          {/* Form Section - Left */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-8 lg:p-16">
            <div className="w-full max-w-md">
              {/* Form Title */}
              <div className="text-center mb-4 md:mb-6 lg:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">
                  Forgot Password?
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  Remember your password?{" "}
                  <MuiLink
                    component="button"
                    onClick={() => navigate("/signIn")}
                    className="text-[#2563EB] hover:text-[#1E40AF] font-medium"
                  >
                    Sign in here
                  </MuiLink>
                </p>
              </div>

              {/* Error Display */}
              {stateOfUser?.error && (
                <div className="mb-3 md:mb-4 lg:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{stateOfUser.error}</p>
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-3 md:space-y-4 lg:space-y-6">
                {/* Email Field */}
                <div>
                  <label
                    htmlFor="emailAddress"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none">
                      <EmailIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                    </div>
                    <input
                      id="emailAddress"
                      type="email"
                      placeholder="Enter your email address"
                      value={formValues?.emailAddress || ""}
                      onChange={handleChange("emailAddress")}
                      autoFocus={true}
                      className="block w-full pl-10 md:pl-12 pr-3 md:pr-4 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all duration-200 text-base md:text-lg"
                    />
                  </div>
                  {formErrors?.emailAddress && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      {formErrors.emailAddress}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={stateOfUser?.loading}
                className="w-full mt-4 md:mt-6 lg:mt-8 py-3 md:py-4 px-4 brand-btn-primary rounded-lg md:rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg ripple"
              >
                {stateOfUser?.loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-2 border-white border-t-transparent mr-2"></div>
                    <span>Sending Code...</span>
                  </div>
                ) : (
                  "Send Verification Code"
                )}
              </button>
            </div>
          </div>

          {/* Blue Design Section - Right - Hidden on Mobile */}
          <div className="hidden lg:block w-1/2 relative overflow-hidden">
            {/* Large Blue Abstract Shape */}
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w:[180%] w-[180%] h-[122%] brand-sidebar rounded-full"></div>

            {/* White Text Overlay */}
            <div className="relative z-10 h-full flex flex-col justify-center items-center px-16">
              <div className="text-white text-center max-w-md">
                <h1 className="text-5xl font-bold mb-4 tracking-wide ml-14">
                  Reset Password
                </h1>
                <p className="text-xl leading-relaxed text-left mb-8 ml-15">
                  Enter your email to receive a verification code and reset your
                  password securely.
                </p>
                <div className="space-y-3 text-left ml-15">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Secure Verification</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Quick Recovery</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Email Confirmation</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small Blue Spheres - Outside Abstract Shape - Hidden on Mobile */}
        <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-12 z-20 ml-4">
          <div className="w-16 h-16 brand-sidebar rounded-full"></div>
          <div className="w-12 h-12 brand-sidebar rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
